/** Heuristic parsing of transcript lines into a draft follow-up (local timezone). */

export type TranscriptCueDraft = {
  next_action: string;
  due_date: string;
  reason: string;
};

const WEEKDAYS: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const ORDINAL_WORDS: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
  eleventh: 11,
  twelfth: 12,
  thirteenth: 13,
  fourteenth: 14,
  fifteenth: 15,
  sixteenth: 16,
  seventeenth: 17,
  eighteenth: 18,
  nineteenth: 19,
  twentieth: 20,
  "twenty-first": 21,
  "twenty-second": 22,
  "twenty-third": 23,
  "twenty-fourth": 24,
  "twenty-fifth": 25,
  "twenty-sixth": 26,
  "twenty-seventh": 27,
  "twenty-eighth": 28,
  "twenty-ninth": 29,
  thirtieth: 30,
  "thirty-first": 31,
};

function stripTime(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function nextWeekdayOnOrAfter(from: Date, targetDow: number): Date {
  const start = stripTime(from);
  for (let i = 0; i < 14; i++) {
    const c = addDays(start, i);
    if (c.getDay() === targetDow) return c;
  }
  return addDays(start, (targetDow - start.getDay() + 7) % 7);
}

function nextDomOnOrAfter(from: Date, dom: number): Date {
  const start = stripTime(from);
  for (let i = 0; i < 62; i++) {
    const c = addDays(start, i);
    if (c.getDate() === dom) return c;
  }
  return start;
}

function combineWeekdayAndDom(weekday: number, dom: number, from: Date): Date | null {
  const start = stripTime(from);
  for (let i = 0; i < 120; i++) {
    const c = addDays(start, i);
    if (c.getDay() === weekday && c.getDate() === dom) return c;
  }
  return null;
}

function atDefaultHour(d: Date, hour = 17): Date {
  const x = new Date(d);
  x.setHours(hour, 0, 0, 0);
  return x;
}

function parseOrdinalToken(t: string): number | null {
  const s = t.toLowerCase().trim();
  if (ORDINAL_WORDS[s] != null) return ORDINAL_WORDS[s];
  const m = s.match(/^(\d{1,2})(st|nd|rd|th)?$/i);
  if (m) {
    const n = parseInt(m[1]!, 10);
    if (n >= 1 && n <= 31) return n;
  }
  return null;
}

function extractOrdinalFromText(lower: string): number | null {
  const m1 = lower.match(/\bthe\s+([a-z0-9-]+)\b/);
  if (m1) {
    const o = parseOrdinalToken(m1[1]!);
    if (o != null) return o;
  }
  const m2 = lower.match(/\bon\s+the\s+(\d{1,2})(st|nd|rd|th)?\b/);
  if (m2) {
    const n = parseInt(m2[1]!, 10);
    if (n >= 1 && n <= 31) return n;
  }
  const m3 = lower.match(/\b(?:i believe,?\s+)?(?:the\s+)?([a-z]+|\d{1,2}(?:st|nd|rd|th)?)\s*$/i);
  if (m3) {
    const o = parseOrdinalToken(m3[1]!.toLowerCase());
    if (o != null) return o;
  }
  return null;
}

function extractWeekday(lower: string): number | null {
  for (const [name, dow] of Object.entries(WEEKDAYS)) {
    if (new RegExp(`\\b${name}\\b`).test(lower)) return dow;
  }
  return null;
}

/** Try to infer due datetime + action label from a single transcript sentence. */
export function parseTranscriptLineToFollowUpDraft(line: string, now = new Date()): TranscriptCueDraft | null {
  const raw = line.trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();

  const weekday = extractWeekday(lower);
  const dom = extractOrdinalFromText(lower);

  let day: Date | null = null;
  if (weekday != null && dom != null) {
    day = combineWeekdayAndDom(weekday, dom, now);
  } else if (weekday != null) {
    day = nextWeekdayOnOrAfter(now, weekday);
  } else if (dom != null) {
    day = nextDomOnOrAfter(now, dom);
  } else if (/\btomorrow\b/.test(lower)) {
    day = addDays(stripTime(now), 1);
  } else if (/\bnext week\b/.test(lower)) {
    day = addDays(stripTime(now), 7);
  }

  if (!day) return null;

  const due = atDefaultHour(day, 17);

  const action =
    weekday != null && dom != null
      ? `Follow up (${day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })})`
      : weekday != null
        ? `Follow up (${day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })})`
        : `Follow up (${day.toLocaleDateString(undefined, { month: "short", day: "numeric" })})`;

  return {
    next_action: action,
    due_date: due.toISOString(),
    reason: `From transcript: ${raw.slice(0, 500)}`,
  };
}

const CUE_SIGNAL =
  /\b(tomorrow|next week|monday|tuesday|wednesday|thursday|friday|saturday|sunday|\d{1,2}:\d{2}\s*(am|pm)|\d{1,2}\s*(am|pm)|\d{1,2}\/\d{1,2}|the\s+(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|twenty-first|twenty-second|twenty-third|twenty-fourth|twenty-fifth|twenty-sixth|twenty-seventh|twenty-eighth|twenty-ninth|thirtieth|thirty-first|\d{1,2}(st|nd|rd|th)?)|on\s+the\s+\d{1,2})\b/i;

export function extractTranscriptCueLines(transcript: string, max = 6): string[] {
  const lines = transcript
    .split(/[.!?\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return lines.filter((l) => CUE_SIGNAL.test(l)).slice(0, max);
}
